import { motion } from "framer-motion";
import {
  MonitorSmartphone,
  Shield,
  Calculator,
  Landmark,
  Clock,
  Globe,
  Info,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { SuggestedPrompt } from "@/types/ai-chat";
import { Logo } from "@/components/common/Logo";

const SUGGESTED_PROMPTS: SuggestedPrompt[] = [
  {
    id: "sp1",
    label: "eTIMS Setup",
    prompt:
      "How do I set up eTIMS for my business? Walk me through the full process.",
    category: "etims",
    icon: "monitor",
  },
  {
    id: "sp2",
    label: "KRA Audit Help",
    prompt: "I received a KRA audit notice. What should I do immediately?",
    category: "audit",
    icon: "shield",
  },
  {
    id: "sp3",
    label: "PAYE Computation",
    prompt:
      "How do I compute PAYE for an employee earning KES 200,000 per month?",
    category: "bookkeeping",
    icon: "calculator",
  },
  {
    id: "sp4",
    label: "VAT Registration",
    prompt:
      "Do I need to register for VAT? My turnover just reached KES 4.5 million.",
    category: "kra-registration",
    icon: "landmark",
  },
  {
    id: "sp5",
    label: "Filing Deadlines",
    prompt: "What are all the KRA filing deadlines I need to know for 2024?",
    category: "tax-returns",
    icon: "clock",
  },
  {
    id: "sp6",
    label: "Transfer Pricing",
    prompt:
      "What are Kenya's transfer pricing documentation requirements for multinationals?",
    category: "international",
    icon: "globe",
  },
];

const PROMPT_ICONS: Record<string, React.ReactNode> = {
  monitor: <MonitorSmartphone size={15} />,
  shield: <Shield size={15} />,
  calculator: <Calculator size={15} />,
  landmark: <Landmark size={15} />,
  clock: <Clock size={15} />,
  globe: <Globe size={15} />,
};

interface WelcomeScreenProps {
  onPromptSelect: (prompt: string) => void;
}

export function WelcomeScreen({ onPromptSelect }: WelcomeScreenProps) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-10 max-w-2xl mx-auto w-full">
      <motion.div
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.38 }}
        className="text-center mb-8"
      >
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Logo variant="light" mode="mark" size="xl" />
        </div>

        <h1 className="text-[22px] sm:text-[26px] font-bold text-[var(--color-charcoal)] mb-2 tracking-tight leading-tight">
          Rosewood <span className="text-gradient-burgundy">AI</span>
        </h1>

        <p className="text-[13px] sm:text-[13.5px]  leading-relaxed max-w-[380px] mx-auto">
          Your Kenya tax compliance assistant. Ask anything about KRA, eTIMS,
          VAT, PAYE, or tax planning.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.38, delay: 0.12 }}
        className="w-full"
      >
        <p className="text-[10px] font-semibold  uppercase tracking-wider text-center mb-3">
          Common questions to get started
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {SUGGESTED_PROMPTS.map((sp, i) => (
            <motion.div
              key={sp.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.18 + i * 0.055 }}
            >
              <Card
                className="cursor-pointer  hover:shadow-xs transition-all duration-200 group bg-white"
                onClick={() => onPromptSelect(sp.prompt)}
              >
                <CardContent className="p-3 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-[var(--radius-sm)] bg-[#FAF0F2] group-hover:bg-[var(--color-burgundy)] flex items-center justify-center flex-shrink-0 transition-colors text-[var(--color-burgundy)] group-hover:text-white">
                    {PROMPT_ICONS[sp.icon]}
                  </div>

                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-[12px] font-semibold text-[var(--color-charcoal)] group-hover:text-[var(--color-burgundy)] transition-colors">
                        {sp.label}
                      </p>
                      <Badge
                        variant="outline"
                        className="text-[9px] h-4 px-1.5 font-semibold border-[var(--color-border)]  hidden sm:inline-flex"
                      >
                        {sp.category}
                      </Badge>
                    </div>
                    <p className="text-[11px]  leading-snug line-clamp-2">
                      {sp.prompt.slice(0, 60)}…
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.58 }}
        className="mt-7 w-full max-w-md"
      >
        <Alert className="border-[var(--color-gold)] bg-[var(--color-gold)]">
          <Info size={13} className="text-white mt-0.5" />
          <AlertDescription className="text-[10.5px] text-white leading-relaxed ml-1">
            Rosewood AI provides general guidance based on Kenyan law. For
            complex matters, always consult a certified CPA. This is not legal
            advice.
          </AlertDescription>
        </Alert>
      </motion.div>
    </div>
  );
}
